import Head from "next/head";
import CLMHero2 from "components/CLM/CLMHero2";
import CTASection from "components/CLM/CTASection";
import EqualDivisionSection from "components/CLM/EqualDivisionSection";
import FAQ2 from "components/CLM/FAQ2";
import { CLMButtonPrimary } from "components/CLM/v3/Buttons";
import Image from "next/image";
import { FaClock, FaSearch } from "react-icons/fa";
import { IoMdSearch } from "react-icons/io";
import { PiBinocularsFill } from "react-icons/pi";
import { BASE_ASSETS_URL } from "@constants/constants";

const customerServiceFaqData = [
  {
    question:
      "How does BoloSign enhance Microsoft Word for contract management?",
    answer:
      "BoloSign offers a seamless Microsoft Word integration that adds powerful contract-specific features. With tools like live collaboration, smart version tracking, and AI-assisted reviews, your contract workflows become faster, more reliable, and easier to manage—directly within Word.",
  },
  {
    question:
      "Can I edit and redline contracts directly in Microsoft Word with BoloSign?",
    answer:
      "Yes, BoloSign allows you to make edits, add redlines, and format contracts right inside Microsoft Word. You don’t need to switch tabs or platforms—everything is tracked, saved automatically, and managed under one unified system.",
  },
  {
    question:
      "How is version control managed in Microsoft Word through BoloSign?",
    answer:
      "BoloSign captures every change made to your contract by creating auto-saved drafts and structured version checkpoints. This helps your team maintain a detailed edit history and track how documents evolve over time.",
  },
  {
    question:
      "How does real-time collaboration work in Microsoft Word with BoloSign?",
    answer:
      "With BoloSign, teams can work on the same contract in real time within Word. You can instantly see who’s making changes, control access, and keep internal discussions private using comment visibility settings.",
  },
  {
    question:
      "Can I manage comments and feedback directly in Microsoft Word using BoloSign?",
    answer:
      "Absolutely. BoloSign lets you organize, review, and control visibility of comments within Word. It makes it simple to address team feedback while keeping internal notes secure and hidden from external collaborators.",
  },
  {
    question:
      "What is AI Verification, and how does it improve contract review in Microsoft Word?",
    answer:
      "AI Verification in BoloSign is designed to enhance contract reviews directly within Microsoft Word. It helps identify key clauses, suggests redlines, and automatically checks for compliance with your internal playbooks. Trained on legal best practices, it enables faster, more accurate first-pass reviews—saving time while ensuring precision.",
  },
];

const CLMHeroData = {
  // title: "Reporting",
  heading: "See what your contracts are really telling you",
  description:
    "With BoloSign, reporting isn’t just numbers—it’s clear, helpful insights that guide smarter decisions and smoother workflows.",
  image: `${BASE_ASSETS_URL}/v2/clm/clm/hero-img-contract-reporting-1.png`,
  btnText: "Take a Quick Demo",
  btnUrl: "/clm/request-demo",
};

const contractReportingData = [
  {
    icon: IoMdSearch,
    text: "Search and pull up the right contracts without wasting time.",
  },
  {
    icon: PiBinocularsFill,
    text: "Keep track of where every contract is in the process.",
  },
  {
    icon: FaClock,
    text: "Use insights to decide what’s next and keep things moving.",
  },
];

const ContractReporting = () => {
  return (
    <>
      <Head>
        <title>Contract Reporting - BoloSign CLM</title>
        <meta
          name="description"
          content="Gain clear insights into your contracts with BoloSign Contract Reporting. Track progress, analyze data, and make informed decisions with ease."
        />
        <meta
          name="keywords"
          content="BoloSign Contract Reporting, contract insights, CLM reporting, contract analytics, contract tracking, contract management, contract data visualization"
        />
        <link rel="canonical" href="https://www.bolosign.com/clm/reporting/" />
      </Head>
      <CLMHero2 CLMHeroData={CLMHeroData} />

      <section className="bg-dark text-white py-12 px-6 md:px-8">
        <h2 className="text-4xl md:text-5xl mb-10 text-center">
          3 Steps in contracts reporting
        </h2>
        <div className="max-w-screen-xl xl:px-8 mx-auto flex flex-col md:flex-row gap-6 justify-between items-center">
          {contractReportingData.map((item, index) => (
            <div
              key={index}
              className="text-center flex flex-col items-center gap-3"
            >
              <item.icon className="text-white" size={48} />
              <div className="bg-[#2A2A2A] p-10 px-12 rounded-xl">
                <p className="text-2xl">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* equaldivision section */}
      <EqualDivisionSection
        // title="Templates"
        heading="Easily search and stay updated on important contracts"
        image={`${BASE_ASSETS_URL}/v2/clm/clm/reporting-search-contract.png`}
        bgColor="bg-transparent"
        imageRight={true}
        checks={[
          "Use smart filters to create views that update automatically when new contracts come in.",
          "Search by specific terms or phrases to quickly find what matters inside your contracts.",
        ]}
      />

      <section className="text-black md:-mt-8 px-6 md:px-8 mb-10 md:mb-28 mt-10 md:mt-0">
        <div className="max-w-screen-xl xl:px-8 mx-auto flex flex-col md:flex-row gap-6 justify-between items-start">
          <div className="text-center flex flex-col items-center md:gap-3 mb-6 md:mb-0">
            <Image
              src={`${BASE_ASSETS_URL}/v2/clm/clm/reporting-filter-by.png`}
              alt="filter-by"
              width={300}
              height={300}
              className="w-full h-full"
            />
            <div className="text-start">
              <p className="text-xl md:text-2xl">
                Filter contracts by dates, revenue, or who they’re with.
              </p>
            </div>
          </div>

          <div className="text-center flex flex-col items-center md:gap-3 mb-6 md:mb-0">
            <Image
              src={`${BASE_ASSETS_URL}/v2/clm/clm/reporting-expiry-is.png`}
              alt="expiry-is"
              width={300}
              height={300}
              className="w-full h-full"
            />
            <div className="text-start">
              <p className="text-xl md:text-2xl">
                Save your favorite searches so you can get to them quickly.
              </p>
            </div>
          </div>

          <div className="text-center flex flex-col items-center md:gap-3 mb-6 md:mb-0">
            <Image
              src={`${BASE_ASSETS_URL}/v2/clm/clm/reporting-custom-view.png`}
              alt="custom-view"
              width={300}
              height={300}
              className="w-full h-full"
            />
            <div className="text-start">
              <p className="text-xl md:text-2xl">
                New contracts that match your filters show up automatically.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* equaldivision section */}
      <EqualDivisionSection
        // title="Templates"
        heading="Edit and automate your reports"
        description="Generate reports just once or set them to run automatically on a schedule—using any view—and have insights delivered straight to your inbox."
        image={`${BASE_ASSETS_URL}/v2/clm/clm/reporting-nearly-expiry.png`}
        bgColor="bg-dark"
        imageRight={false}
        checks={[
          "Use advanced filters and search tools to create personalized data views.",
          "Schedule recurring or on-demand reports directly from your saved views.",
          "Distribute vital contract data across departments and decision-makers.",
        ]}
      />

      {/* equaldivision section */}
      <EqualDivisionSection
        // title="Templates"
        heading="Optimize your contract workflows"
        description="Track every stage of your contract lifecycle—from drafting to completion—with clear, actionable analytics that help you."
        // image={`${BASE_ASSETS_URL}/v2/clm/clm/average-time-taken.png`}
        image={`${BASE_ASSETS_URL}/v2/clm/clm/reporting-avg-time.png`}
        bgColor="bg-transparent"
        imageRight={true}
        checks={[
          "Spot bottlenecks in your workflows and streamline processes to speed up contract turnaround times.",
          "Use concrete data to showcase how the legal team contributes to business growth and operational efficiency.",
          "Understand team workloads, forecast resource needs, and make smarter staffing or process decisions.",
        ]}
      />

      {/* FAQ Section */}
      <section className="bg-[#f4f2f0] text-black py-16 md:px-10 px-4 xl:px-20 text-center md:pt-24 pt-12 ">
        <FAQ2 faqData={customerServiceFaqData} heading={"FAQs"} />
      </section>
    </>
  );
};

export default ContractReporting;
